import { ChatBotWrapper, ReactQueryContainer } from '@retail/containers';
import { AuthContextProvider, AuthProvider } from '@retail/auth/data-access';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { RecoilRoot } from 'recoil';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { mgPrisConfig } from '@retail/mgpris/config';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { mgPrisTheme } from '@shared/styles';
import { AppRoutes } from './AppRoutes';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { COLOR_SCHEME_MODE_STORAGE_KEY, COLOR_SCHEME_STORAGE_KEY } from '@shared/components';
import { AuthContainerPage, OrgUnitAccessChecker } from '@retail/app/pages';
import { FeatureFlag } from '@retail/app/stores/selected-context';
import {
  AnalyticsSetupContainer,
  BrandsProviderImpl,
  MyOrgUnitAssortmentsProviderImpl,
  SelectedOrgUnitProviderImpl,
} from '@retail/app/containers';
import { isRunnningLocally } from './isRunningLocally';
import { LocalizationProviderWrapper } from './LocalizationProviderWrapper';

const globalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      code: {
        color: theme.vars.palette.error.main,
        backgroundColor: theme.vars.palette.grey['100'],
        padding: '4px 6px',
        borderRadius: '7px',
      },
    })}
  />
);

export function App() {
  const { env } = useMgPrisConfig();

  return (
    <ThemeProvider
      theme={mgPrisTheme}
      defaultMode="light"
      modeStorageKey={COLOR_SCHEME_MODE_STORAGE_KEY}
      colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      disableTransitionOnChange
      noSsr
    >
      {mgPrisConfig.clarity?.id && !isRunnningLocally && (
        <HelmetProvider>
          <Helmet>
            <script type="text/javascript">
              {`(function(c, l, a, r, i, t, y) {
                 c[a] = c[a] || function() {(c[a].q = c[a].q || []).push(arguments)};
                 t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
                 y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
               })(window, document, "clarity", "script", "${mgPrisConfig.clarity.id}");`}
            </script>
          </Helmet>
        </HelmetProvider>
      )}

      {globalStyles}
      <CssBaseline />
      <ReactQueryContainer showDevtools={env.includes('local')}>
        <RecoilRoot>
          <AuthProvider>
            <AuthContextProvider>
              <AuthContainerPage>
                {({ appTenants }) => (
                  <SuspenseWithSentryErrorBoundary>
                    <BrandsProviderImpl appTenants={appTenants}>
                      <MyOrgUnitAssortmentsProviderImpl>
                        <OrgUnitAccessChecker>
                          <SelectedOrgUnitProviderImpl>
                            <LocalizationProviderWrapper>
                              <AppRoutes />
                              <FeatureFlag feature="chatbot">
                                <ChatBotWrapper
                                  mgAppsBffUrl={mgPrisConfig.mgAppsBff.url}
                                  mgAppsBffAudience={mgPrisConfig.auth0.mgAppsBffAudience}
                                />
                              </FeatureFlag>
                              <AnalyticsSetupContainer />
                            </LocalizationProviderWrapper>
                            <ToastContainer />
                          </SelectedOrgUnitProviderImpl>
                        </OrgUnitAccessChecker>
                      </MyOrgUnitAssortmentsProviderImpl>
                    </BrandsProviderImpl>
                  </SuspenseWithSentryErrorBoundary>
                )}
              </AuthContainerPage>
            </AuthContextProvider>
          </AuthProvider>
        </RecoilRoot>
      </ReactQueryContainer>
    </ThemeProvider>
  );
}

export default App;
